import { useFinancialDocument } from "@api/financialDocument.api";
import { BalanceSheetAccountClass } from "@common/constants/domains/accounting/accounts";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { FinancialDocumentPreview } from "@components/documents/FinancialDocumentDetails/FinancialDocumentPreview";
import {
    StepTrailBreadcrumbs,
    type TrailStep,
} from "@components/breadcrumbs/StepTrailBreadcrumbs";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useHelpCenterUrl } from "@hooks/useHelpCenterUrl";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import classNames from "classnames";
import React from "react";
import { BalanceSheetCheck } from "./BalanceSheetCheck";
import styles from "./BalanceSheetImporter.module.scss";
import { BalanceSheetImporterSpreadsheet } from "./BalanceSheetImporterSpreadsheet";
import { Footer } from "./Footer";
import footerStyles from "./Footer.module.scss";
import { AccountProxyObject, BalanceSheetImportStep } from "./types";
import { UploadDocumentStep } from "./UploadDocumentStep";
import { ValidationResult } from "./validation";

const STEPS: ReadonlyArray<TrailStep<BalanceSheetImportStep>> = [
    { id: BalanceSheetImportStep.UploadDocument, label: "Upload" },
    { id: BalanceSheetImportStep.EnterBalances, label: "Extract" },
    { id: BalanceSheetImportStep.MapAccounts, label: "Map" },
];

interface Props {
    loading: boolean;
    availableClassifications?: BalanceSheetAccountClass[];
    onDocumentSelect: (documentId: number) => void;
    isExtracting: boolean;
    isMapping: boolean;
    accounts: AccountProxyObject[];
    onAccountsChange: (accounts: AccountProxyObject[]) => void;
    onSave: () => void;
    validationResult: ValidationResult;
    hasAttemptedSubmit: boolean;
    step: BalanceSheetImportStep;
    onStepChange: (step: BalanceSheetImportStep) => void;
    bookkeepingStartDate: string;
    extractedDate: string | null;
    onExtractedDateChange: (date: string | null) => void;
    uploadedDocumentId: number | null;
}

export const BalanceSheetImporterModalContent: React.FC<Props> = ({
    loading,
    availableClassifications,
    onDocumentSelect,
    isExtracting,
    isMapping,
    accounts,
    onAccountsChange,
    onSave,
    validationResult,
    hasAttemptedSubmit,
    step,
    onStepChange,
    bookkeepingStartDate,
    extractedDate,
    onExtractedDateChange,
    uploadedDocumentId,
}) => {
    const { data: uploadedDocument } = useFinancialDocument(
        uploadedDocumentId ?? undefined,
    );
    const { selectedEntity } = useAccountingTabContext();
    const helpCenterUrl = useHelpCenterUrl();

    const dateErrorMessage = validationResult.errors.balanceSheetDate;

    const header = (
        <StandardModalHeader className={styles.headerWithBreadcrumbs}>
            <span>Import Balance Sheet</span>
            <StepTrailBreadcrumbs
                steps={STEPS}
                currentStep={step}
                onStepChange={onStepChange}
            />
        </StandardModalHeader>
    );

    if (!selectedEntity) {
        return null;
    }

    if (step === BalanceSheetImportStep.UploadDocument) {
        return (
            <>
                {header}
                <StandardModalBody className={styles.uploadStepModalBody}>
                    <UploadDocumentStep
                        onDocumentSelect={onDocumentSelect}
                        bookkeepingStartDate={bookkeepingStartDate}
                        isExtracting={isExtracting}
                    />
                </StandardModalBody>
                {helpCenterUrl && (
                    <div className={footerStyles.modalFooter}>
                        <CommandIsland className={footerStyles.commandIsland}>
                            <a
                                href={helpCenterUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={footerStyles.helpCenterLink}
                            >
                                <span>Questions?</span>{" "}
                                <span
                                    className={footerStyles.helpCenterLinkBold}
                                >
                                    Open Help Center
                                </span>
                            </a>
                        </CommandIsland>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            {header}
            <StandardModalBody className={styles.modalBody}>
                <div
                    className={classNames(styles.contentContainer, {
                        [styles.withPreview]:
                            uploadedDocument &&
                            step === BalanceSheetImportStep.EnterBalances,
                    })}
                >
                    {uploadedDocument &&
                        step === BalanceSheetImportStep.EnterBalances && (
                            <div className={styles.previewPanel}>
                                <FinancialDocumentPreview
                                    financialDocument={uploadedDocument}
                                    compact
                                    withFileName
                                />
                            </div>
                        )}
                    <div className={styles.rightPanel}>
                        {step === BalanceSheetImportStep.EnterBalances && (
                            <div className={styles.dateSection}>
                                <p className={styles.dateSectionDescription}>
                                    Please review the extracted date and
                                    accounts to continue.
                                </p>
                                <div className={styles.datePicker}>
                                    <label className={styles.dateLabel}>
                                        Balance sheet date
                                    </label>
                                    <DatePicker
                                        mode="day"
                                        variant="panel-input"
                                        placeholder="Select date"
                                        value={extractedDate ?? undefined}
                                        onValueChange={(newDate) =>
                                            onExtractedDateChange(
                                                newDate ?? null,
                                            )
                                        }
                                        buttonProps={{
                                            size: "md",
                                            className: classNames({
                                                [styles.dateInputError]:
                                                    !!dateErrorMessage,
                                            }),
                                        }}
                                    />
                                </div>
                                {dateErrorMessage && (
                                    <span className={styles.dateWarning}>
                                        {dateErrorMessage}
                                    </span>
                                )}
                            </div>
                        )}
                        {step === BalanceSheetImportStep.MapAccounts && (
                            <div className={styles.mapAccountsSection}>
                                <p className={styles.dateSectionDescription}>
                                    For each account, map to an existing Kick
                                    account, or create a new one.
                                </p>
                            </div>
                        )}
                        <BalanceSheetImporterSpreadsheet
                            availableClassifications={availableClassifications}
                            accounts={accounts}
                            onAccountsChange={onAccountsChange}
                            validationErrors={validationResult.errors}
                            hasAttemptedSubmit={hasAttemptedSubmit}
                            step={step}
                            extractedDate={extractedDate}
                            isMapping={isMapping}
                        />
                        {step === BalanceSheetImportStep.EnterBalances && (
                            <BalanceSheetCheck
                                accounts={accounts}
                                validationResult={validationResult}
                            />
                        )}
                    </div>
                </div>
                <Footer
                    loading={loading}
                    onSubmit={onSave}
                    validationResult={validationResult}
                    step={step}
                    onNext={() =>
                        onStepChange(BalanceSheetImportStep.MapAccounts)
                    }
                    onBack={() =>
                        onStepChange(
                            step === BalanceSheetImportStep.MapAccounts
                                ? BalanceSheetImportStep.EnterBalances
                                : BalanceSheetImportStep.UploadDocument,
                        )
                    }
                    hasAttemptedSubmit={hasAttemptedSubmit}
                />
            </StandardModalBody>
        </>
    );
};
